import type { FC, ReactNode } from 'react'
import type { StoreBase } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import {
    EyeOff,
    Flame,
    CircleHelp,
    ShoppingBag,
    Sparkles,
    Tag,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'

import { useCart } from './cart-context'
import { useMascot } from './mascot-context'
import { useStage } from './stage-context'
import { MascotAvatar } from './mascot-avatar'
import { MascotView3D } from './three-loaders'

interface MascotAssistantProps {
    store?: StoreBase | null
}

const PAGE_TIPS: Record<string, string> = {
    '/produk/[slug]': 'Geser kemasannya ke kiri atau kanan untuk melihat dari semua sisi, ya!',
    '/keranjang': 'Isi nomor WhatsApp yang aktif supaya admin bisa konfirmasi ongkir dan pembayaran.',
    '/produk': 'Pakai filter kategori atau kolom pencarian biar camilan favoritmu cepat ketemu.'
}

/* The floating Gemar Jajan boy: greets, shares promos, gives tips and
   celebrates every time something lands in the cart. */
export const MascotAssistant: FC<MascotAssistantProps> = ({ store }): ReactNode => {
    const router = useRouter()
    const mascot = useMascot()
    const cart = useCart()
    const { status, reducedMotion } = useStage()
    const [mounted, setMounted] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [visible, setVisible] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const lastCelebrated = useRef(0)
    const settings = store?.settings

    useEffect(() => {
        setMounted(true)

        if (sessionStorage.getItem('gj-mascot-hidden') === '1') setHidden(true)
    }, [])

    useEffect(() => {
        if (router.pathname !== '/') {
            setVisible(true)

            return
        }

        const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.65)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [router.pathname])

    useEffect(() => {
        if (!mounted || !visible || hidden || sessionStorage.getItem('gj-greeted')) return

        const timer = window.setTimeout(() => {
            sessionStorage.setItem('gj-greeted', '1')
            mascot.say(
                settings?.mascot_greeting
                    ? <span data-mdlfy-cms={`store_settings:${settings.id}:mascot_greeting`}>{settings.mascot_greeting}</span>
                    : 'Halo, Sobat Jajan! Mau camilan apa hari ini?',
                'wave',
                7500
            )
        }, router.pathname === '/' ? 900 : 2200)

        return () => window.clearTimeout(timer)
        // greet once per visit, as soon as the mascot is on screen
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mounted, visible, hidden])

    useEffect(() => {
        if (!mounted) return

        const tip = PAGE_TIPS[router.pathname]
        const key = `gj-tip-${router.pathname}`

        if (!tip || sessionStorage.getItem(key)) return

        const timer = window.setTimeout(() => {
            sessionStorage.setItem(key, '1')
            mascot.say(tip, 'think', 7500)
        }, 5000)

        return () => window.clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mounted, router.pathname])

    useEffect(() => {
        const added = cart.lastAdded

        if (!added || added.at === lastCelebrated.current) return

        lastCelebrated.current = added.at
        setVisible(true)
        mascot.say(
            <>
                Mantap! <strong className='font-semibold' data-mdlfy-cms={`products:${added.item.productId}:name`}>{added.item.name}</strong> sudah masuk keranjang.
            </>,
            'celebrate',
            4500
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart.lastAdded])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    if (!mounted) return null

    if (hidden) {
        return (
            <button
                type='button'
                onClick={() => {
                    sessionStorage.removeItem('gj-mascot-hidden')
                    setHidden(false)
                    mascot.say('Aku kembali! Ada yang bisa kubantu?', 'wave')
                }}
                className='fixed bottom-4 right-4 z-40 size-12 rounded-full shadow-pop transition-transform hover:-translate-y-0.5'
                aria-label='Tampilkan maskot'
            >
                <MascotAvatar className='size-12' />
            </button>
        )
    }

    const go = (href: string, message?: ReactNode) => {
        setMenuOpen(false)
        router.push(href)

        if (message) mascot.say(message, 'wave')
    }

    const actions = [
        {
            label: 'Promo hari ini',
            icon: Tag,
            run: () => go('/#promo', settings?.announcement
                ? <span data-mdlfy-cms={`store_settings:${settings.id}:announcement`}>{settings.announcement}</span>
                : 'Cek harga spesial di bagian Promo Hari Ini, ya!')
        },
        { label: 'Produk terlaris', icon: Flame, run: () => go('/produk?kategori=produk-terlaris', 'Ini camilan yang paling sering dipesan Sobat Jajan!') },
        { label: 'Bantu pilih camilan', icon: Sparkles, run: () => go('/#rekomendasi') },
        { label: 'Cara pesan', icon: CircleHelp, run: () => go('/#cara-pesan', 'Pilih camilan, isi data pengiriman, lalu kirim pesanan lewat WhatsApp. Gampang!') },
        {
            label: 'Lihat keranjang',
            icon: ShoppingBag,
            run: () => {
                setMenuOpen(false)
                cart.openDrawer()
            }
        },
        {
            label: 'Sembunyikan maskot',
            icon: EyeOff,
            run: () => {
                setMenuOpen(false)
                mascot.dismiss()
                sessionStorage.setItem('gj-mascot-hidden', '1')
                setHidden(true)
            }
        }
    ]

    const show3d = status === 'ready'
    const use3d = status === 'loading' || status === 'ready'

    return (
        <div
            className={cn(
                'pointer-events-none fixed bottom-2 right-2 z-40 flex flex-col items-end transition-all duration-500 sm:bottom-4 sm:right-4',
                visible ? 'opacity-100' : 'translate-y-6 opacity-0'
            )}
            aria-hidden={visible ? undefined : true}
        >
            {menuOpen && (
                <div className={cn('mb-2 mr-2 w-60 animate-pop-in rounded-3xl border-2 border-brand-choco bg-white p-2 shadow-pop', visible && 'pointer-events-auto')} role='menu' aria-label='Bantuan maskot'>
                    {actions.map(action => (
                        <button
                            key={action.label}
                            type='button'
                            role='menuitem'
                            onClick={action.run}
                            className='flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm font-semibold text-brand-choco transition-colors hover:bg-brand-cream focus-visible:bg-brand-cream focus-visible:outline-none'
                        >
                            <action.icon className='size-4 text-brand-orange' aria-hidden='true' />
                            {action.label}
                        </button>
                    ))}
                </div>
            )}

            {!menuOpen && mascot.message && (
                <div
                    key={mascot.message.id}
                    className={cn('relative mb-1 mr-3 max-w-[250px] animate-pop-in rounded-3xl border-2 border-brand-choco bg-white py-3 pl-4 pr-9 text-sm leading-relaxed text-brand-choco shadow-pop sm:max-w-[280px]', visible && 'pointer-events-auto')}
                    role='status'
                >
                    {mascot.message.content}
                    <button
                        type='button'
                        onClick={mascot.dismiss}
                        className='absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded-full text-brand-choco/60 hover:bg-brand-sand hover:text-brand-choco'
                        aria-label='Tutup pesan maskot'
                    >
                        <X className='size-3.5' />
                    </button>
                    <span className='absolute -bottom-[9px] right-12 size-4 rotate-45 border-b-2 border-r-2 border-brand-choco bg-white' aria-hidden='true' />
                </div>
            )}

            <button
                type='button'
                onClick={() => {
                    setMenuOpen(open => !open)
                    mascot.react('wave')
                }}
                className={cn('relative h-[118px] w-[96px] rounded-3xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/40 sm:h-[150px] sm:w-[124px]', visible && 'pointer-events-auto')}
                tabIndex={visible ? 0 : -1}
                aria-label='Buka bantuan maskot GEMAR JAJAN'
                aria-expanded={menuOpen}
            >
                {!show3d && (
                    <MascotAvatar className={cn('absolute bottom-1 right-1 size-[76px] shadow-pop sm:size-[88px]', !reducedMotion && 'animate-float')} />
                )}
                {use3d && visible && (
                    <MascotView3D
                        className='absolute inset-0'
                        mood={mascot.mood}
                        moodKey={mascot.moodKey}
                        reducedMotion={reducedMotion}
                    />
                )}
            </button>
        </div>
    )
}
